import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, ArrowRight, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import Navbar from './landing/Navbar';
import Footer from './landing/Footer';
import { Container, SectionHeading, GlassCard, fadeInUp } from './landing/shared';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      await axios.post(API_ENDPOINTS.public.contact, formData);
      setSubmitStatus('success');
      setSubmitMessage(t('contact.form.success'));
      setFormData({ name: '', email: '', company: '', message: '' });
      trackEvent(AnalyticsEvents.CONTACT_MESSAGE, { status: 'success' });
    } catch (error: any) {
      setSubmitStatus('error');
      setSubmitMessage(error.response?.data?.error || t('contact.form.error'));
      trackEvent(AnalyticsEvents.CONTACT_MESSAGE, { status: 'error', error: error.response?.status });
    } finally {
      setSubmitting(false);
    }
  };

  const CONTACT_INFO = [
    { icon: Mail, title: t('contact.getInTouch.email'), value: 'alenis.services@gmail.com', sub: t('contact.getInTouch.emailResponse') },
    { icon: Phone, title: t('contact.getInTouch.phone'), value: '+48 XXX XXX XXX', sub: t('contact.getInTouch.phoneHours') },
    { icon: MapPin, title: t('contact.getInTouch.office'), value: 'Warsaw, Poland', sub: t('contact.getInTouch.officeDesc') },
  ];

  const FAQS = ['q1', 'q2', 'q3', 'q4'];

  return (
    <div className="min-h-screen overflow-hidden bg-brand-gradient text-white">
      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden pb-20 pt-16 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-1/2 top-[-10%] h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-brand-primary/20 blur-[140px]" />
            <div className="absolute right-[-10%] top-[20%] h-[350px] w-[450px] rounded-full bg-brand-secondary/10 blur-[120px]" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]" />
          </div>

          <Container className="relative text-center">
            <motion.span
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-secondary backdrop-blur-xl"
            >
              {t('navigation.home')} · {t('contact.title')}
            </motion.span>

            <motion.h1
              variants={fadeInUp}
              custom={1}
              initial="hidden"
              animate="visible"
              className="mx-auto max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {t('contact.title')}
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              custom={2}
              initial="hidden"
              animate="visible"
              className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-brand-muted sm:text-xl"
            >
              {t('contact.subtitle')}
            </motion.p>
          </Container>
        </section>

        {/* Contact Form + Info */}
        <section className="relative py-24 sm:py-32">
          <Container>
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
              {/* Form */}
              <GlassCard index={0} className="p-8 sm:p-10">
                <h2 className="text-2xl font-bold text-white sm:text-3xl">{t('contact.form.title')}</h2>

                {submitStatus === 'success' && (
                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-green-400/20 bg-green-500/10 p-4">
                    <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                    <p className="text-sm text-green-300">{submitMessage}</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-400/20 bg-red-500/10 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
                    <p className="text-sm text-red-300">{submitMessage}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-2 block text-sm font-medium text-brand-muted">
                        {t('contact.form.name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        placeholder={t('contact.form.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-brand-muted">
                        {t('contact.form.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="mb-2 block text-sm font-medium text-brand-muted">
                      {t('contact.form.company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                      placeholder={t('contact.form.companyPlaceholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-brand-muted">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full resize-none rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder-brand-muted/60 backdrop-blur-xl transition-colors focus:border-brand-primary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-button px-8 py-4 text-base font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {submitting ? (
                      t('contact.form.sending')
                    ) : (
                      <>
                        {t('contact.form.send')}
                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              </GlassCard>

              {/* Info */}
              <div className="flex flex-col gap-8">
                <div>
                  <h2 className="text-2xl font-bold text-white sm:text-3xl">{t('contact.getInTouch.title')}</h2>
                  <p className="mt-4 text-lg leading-relaxed text-brand-muted">{t('contact.getInTouch.description')}</p>
                </div>

                <div className="grid gap-4">
                  {CONTACT_INFO.map(({ icon: Icon, title, value, sub }, i) => (
                    <GlassCard key={title} index={i + 1} className="flex items-start gap-4 p-6">
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-button shadow-glow">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{title}</h3>
                        <p className="mt-1 text-brand-muted">{value}</p>
                        <p className="mt-1 text-sm text-brand-muted/70">{sub}</p>
                      </div>
                    </GlassCard>
                  ))}
                </div>

                {/* Quick Actions */}
                <GlassCard index={4} className="p-6">
                  <h3 className="text-lg font-semibold text-white">{t('contact.quickActions.title')}</h3>
                  <div className="mt-4 flex flex-col gap-3">
                    <Link
                      to="/register"
                      className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-button px-6 py-3 text-sm font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.02]"
                    >
                      {t('contact.quickActions.startTrial')}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                      to="/about"
                      className="inline-flex items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.04] px-6 py-3 text-sm font-semibold text-white backdrop-blur-xl transition-colors duration-300 hover:bg-white/[0.08]"
                    >
                      {t('contact.quickActions.learnMore')}
                    </Link>
                  </div>
                </GlassCard>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ */}
        <section className="relative py-24 sm:py-32">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute right-[-10%] top-[30%] h-[400px] w-[500px] rounded-full bg-brand-secondary/10 blur-[140px]" />
          </div>

          <Container className="relative">
            <SectionHeading
              eyebrow={t('contact.faq.title')}
              title={t('contact.faq.title')}
              subtitle={t('contact.faq.subtitle')}
            />

            <div className="mt-16 grid gap-6 md:grid-cols-2">
              {FAQS.map((key, i) => (
                <GlassCard key={key} index={i}>
                  <h3 className="text-lg font-semibold text-white">{t(`contact.faq.${key}`)}</h3>
                  <p className="mt-3 leading-relaxed text-brand-muted">{t(`contact.faq.a${key.slice(1)}`)}</p>
                </GlassCard>
              ))}
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
